import { Icons, Input, List, ListItem, ListItemButton, ListItemContent, Switch, Typography } from '@healthinal/ui';
import React, { useEffect, useRef, useState } from 'react';
import { PatientBasicsDto } from '../../api/generated/model';
import { ContentSection } from '../../components/ContentSection.tsx';
import { MotionDivider } from '../../components/MotionDivider.tsx';
import { StickyContainer } from '../../components/StickyContainer.tsx';
import { useCustomVirtualizer } from '../../hooks/useCustomVirtualizer.ts';
import { PATIENT_SIDE_LIST_ITEM_HEIGHT } from '../../theme.ts';
import { formatDateYear } from '../../utils/date.ts';
import {
  searchSideListPatient,
  updatePatientSideListSearchData,
} from '../../utils/patientSideListSearch/patientSideListSearch.ts';

interface Props {
  patients: PatientBasicsDto[];
  searchText: string;
  setSearchText: (text: string) => void;
  selectedPatient: PatientBasicsDto | null;
  setSelectedPatient: (patient: PatientBasicsDto | null) => void;
  activeOnly?: boolean;
  setActiveOnly?: (activeOnly: boolean) => void;
  renderPatientLabel?: (patient: PatientBasicsDto) => React.ReactNode;
  useClientSideSearch?: boolean;
}
export function PatientSideListNav({
  patients,
  searchText,
  setSearchText,
  selectedPatient,
  setSelectedPatient,
  activeOnly,
  setActiveOnly,
  renderPatientLabel,
  useClientSideSearch = true,
}: Props) {
  const contentSectionRef = useRef(null);

  const [filteredPatients, setFilteredPatients] = useState(patients);

  useEffect(() => {
    if (!useClientSideSearch) {
      return;
    }
    updatePatientSideListSearchData(patients);
  }, [patients, useClientSideSearch]);

  useEffect(() => {
    if (!useClientSideSearch) {
      setFilteredPatients(patients);
      return;
    }
    if (!searchText) {
      setFilteredPatients(patients);
      return;
    }
    let ignore = false;
    void searchSideListPatient(searchText).then((foundPatients) => {
      if (!ignore) {
        setFilteredPatients(foundPatients);
      }
    });
    return () => {
      ignore = true;
    };
  }, [searchText, patients, useClientSideSearch]);

  const { virtualItems, totalSize, paddingTop, paddingBottom, measureElement } = useCustomVirtualizer({
    count: filteredPatients.length,
    cellHeight: PATIENT_SIDE_LIST_ITEM_HEIGHT,
    parentRef: contentSectionRef,
  });

  return (
    <ContentSection flex={1} ref={contentSectionRef}>
      <StickyContainer>
        <Input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={'Patient suchen'}
          endDecorator={<Icons.Search />}
          sx={{ my: 1 }}
        />
        {activeOnly !== undefined && (
          <div>
            <Switch
              checked={!activeOnly}
              onClick={() => setActiveOnly && setActiveOnly(!activeOnly)}
              endDecorator={'Erledigte anzeigen'}
            />
          </div>
        )}
        <List sx={{ '--ListItem-radius': '8px', textDecoration: 'none' }}>
          <ListItem>
            <ListItemButton selected={!selectedPatient} onClick={() => setSelectedPatient(null)}>
              Alle Patienten
            </ListItemButton>
          </ListItem>
        </List>
        <MotionDivider contentSectionRef={contentSectionRef} />
      </StickyContainer>
      <List
        sx={{
          '--ListItem-radius': '8px',
          textDecoration: 'none',
          height: `${totalSize}px`,
          paddingTop: `${paddingTop}px`,
          paddingBottom: `${paddingBottom}px`,
        }}>
        {virtualItems.map((virtualRow) => {
          const patient = filteredPatients[virtualRow.index];
          return (
            <ListItem key={virtualRow.key} data-index={virtualRow.index} ref={measureElement}>
              <ListItemButton selected={selectedPatient?.id === patient.id} onClick={() => setSelectedPatient(patient)}>
                <ListItemContent>
                  <Typography fontSize={16}>
                    {renderPatientLabel ? (
                      renderPatientLabel(patient)
                    ) : (
                      <>
                        {patient.caseNumber} {patient.firstName} {patient.lastName}{' '}
                        <Typography level={'body-sm'}>{formatDateYear(patient.birthDate)}</Typography>
                      </>
                    )}
                  </Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </ContentSection>
  );
}
